import { Link } from 'react-router-dom';
import { useState } from 'react';
import SchemaOrg from '../../components/feature/SchemaOrg';
import { useSEO } from '../../hooks/useSEO';
import QuickFAQSection from './components/QuickFAQSection';
import AISearchOptimizedSection from './components/AISearchOptimizedSection';
import VoiceSearchFAQSection from './components/VoiceSearchFAQSection';
import PopularTagsSection from './components/PopularTagsSection';
import GoogleReviewsSection from './components/GoogleReviewsSection';
import TestimonialCarouselSection from './components/TestimonialCarouselSection';
import FeaturedCasesSection from './components/FeaturedCasesSection';
import MovingProcessSection from './components/MovingProcessSection';
import PricingSection from './components/PricingSection';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import ServicesSection from './components/ServicesSection';
import OptimizedImage from '../../components/base/OptimizedImage';

const HomePage = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const navLinks = [
    { to: '/', label: '首頁', icon: 'ri-home-line' },
    { to: '/about', label: '關於我們', icon: 'ri-information-line' },
    { to: '/services', label: '服務項目', icon: 'ri-service-line' },
    { to: '/pricing', label: '價格方案', icon: 'ri-price-tag-3-line' },
    { to: '/price-comparison', label: '價格比較', icon: 'ri-bar-chart-line' },
    { to: '/cases', label: '搬家案例', icon: 'ri-gallery-line' },
    { to: '/service-area', label: '服務地區', icon: 'ri-map-pin-line' },
    { to: '/knowledge', label: '搬家知識', icon: 'ri-book-open-line' },
    { to: '/faq', label: '常見問題', icon: 'ri-question-line' },
    { to: '/contact', label: '聯絡我們', icon: 'ri-contacts-line' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* ✅ 只保留一個 Schema.org 標籤 - LocalBusiness 類型 */}
      <SchemaOrg 
        type="LocalBusiness"
        data={{
          name: '建鈞精緻搬家',
          url: siteUrl,
          telephone: '+886-978-900-081',
          priceRange: '$$',
          address: {
            streetAddress: '福德街49號3樓',
            addressLocality: '泰山區',
            addressRegion: '新北市',
            postalCode: '243',
            addressCountry: 'TW'
          },
          geo: {
            latitude: 25.0330,
            longitude: 121.5654
          },
          openingHours: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
              opens: '00:00',
              closes: '23:59'
            }
          ],
          areaServed: [
            {
              '@type': 'City',
              name: '新北市',
              geo: {
                '@type': 'GeoCoordinates',
                latitude: 25.0142,
                longitude: 121.4627
              }
            },
            {
              '@type': 'City',
              name: '台北市',
              geo: {
                '@type': 'GeoCoordinates',
                latitude: 25.033,
                longitude: 121.5654
              }
            },
            {
              '@type': 'City',
              name: '桃園市',
              geo: {
                '@type': 'GeoCoordinates',
                latitude: 24.9936,
                longitude: 121.301
              }
            },
            {
              '@type': 'City',
              name: '新竹市',
              geo: {
                '@type': 'GeoCoordinates',
                latitude: 24.8138,
                longitude: 120.9675
              }
            }
          ],
          hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: '搬家服務項目',
            itemListElement: [
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: '家庭搬家',
                  description: '提供專業的家庭搬家服務，包含打包、搬運、定位等完整服務'
                }
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: '辦公室搬遷',
                  description: '專業辦公室搬遷服務，確保設備安全，減少營運中斷時間'
                }
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: '廢棄物處理',
                  description: '提供廢棄物清運與處理服務，環保合法處理'
                }
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: '精緻包裝',
                  description: '專業包裝服務，保護貴重物品安全運送'
                }
              }
            ]
          }
        }}
      />
      
      {/* 🚀 統一導航選單 - 電腦版和手機版 */}
      <div className="fixed top-0 left-0 right-0 bg-white shadow-md z-50">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img 
              src="https://static.readdy.ai/image/9ef47c451bf9aafd7a63eea67af7d95d/e5fea51548a5e186b09c2cfef32a0653.jpeg"
              alt="建鈞精緻搬家 Logo"
              className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
            <span className="text-lg font-bold text-teal-600">
              建鈞精緻搬家
            </span>
          </Link>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            aria-label="選單"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* 下拉選單 */}
        {isMobileMenuOpen && (
          <div className="bg-white border-t border-gray-200 shadow-lg max-h-[80vh] overflow-y-auto">
            <div className="px-4 py-3 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all cursor-pointer font-medium"
                >
                  <i className={`${link.icon} text-xl`}></i>
                  <span>{link.label}</span>
                </Link>
              ))}
              
              {/* 聯絡按鈕 */}
              <div className="pt-3 mt-3 border-t border-gray-200 space-y-2">
                <a
                  href="tel:0978900081"
                  className="flex items-center justify-center gap-2 w-full bg-teal-600 text-white px-4 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <i className="ri-phone-line text-lg"></i>
                  <span>0978-900-081</span>
                </a>
                <a
                  href="https://line.me/ti/p/@285yavdh"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full bg-green-600 text-white px-4 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <img 
                    src="https://static.readdy.ai/image/9ef47c451bf9aafd7a63eea67af7d95d/c33a3c3f2fa31df7dfad05bfabc09369.webp" 
                    alt="LINE" 
                    className="w-5 h-5"
                    loading="lazy"
                    decoding="async"
                  />
                  <span>官方LINE</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 🎯 浮動 LINE 按鈕 - 右下角固定 */}
      <a
        href="https://line.me/ti/p/@285yavdh"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 group"
        aria-label="加入官方 LINE"
      >
        <div className="relative">
          {/* 脈衝動畫背景 */}
          <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-75"></div>
          
          {/* LINE 按鈕 */}
          <div className="relative bg-gradient-to-br from-green-500 to-green-600 w-16 h-16 rounded-full flex items-center justify-center shadow-2xl hover:shadow-green-500/50 hover:scale-110 transition-all duration-300">
            <img 
              src="https://static.readdy.ai/image/9ef47c451bf9aafd7a63eea67af7d95d/c33a3c3f2fa31df7dfad05bfabc09369.webp" 
              alt="LINE" 
              className="w-8 h-8"
              loading="lazy"
              decoding="async"
            />
          </div>
          
          {/* 提示文字 */}
          <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="font-bold text-sm">立即諮詢</div>
            <div className="text-xs">點擊加入官方 LINE</div>
            {/* 箭頭 */}
            <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-l-8 border-l-green-600"></div>
          </div>
        </div>
      </a>

      <main className="pt-14">
        <HeroSection />
        <AboutSection />
        <ServicesSection />
        <MovingProcessSection />
        <PricingSection />
        
        {/* Price Comparison CTA */}
        <section className="py-12 bg-gradient-to-r from-teal-50 to-emerald-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center">
                <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full mb-4">
                  <i className="ri-bar-chart-line"></i>
                  <span className="text-sm font-semibold">價格透明化</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  全台搬家價格一次看懂
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  16縣市搬家費用完整比較｜了解不同地區的價格差異｜找到最適合您的搬家方案
                </p>
                <Link
                  to="/price-comparison"
                  className="inline-flex items-center gap-2 bg-teal-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  <i className="ri-price-tag-3-line text-xl"></i>
                  查看價格比較表
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>
        
        <FeaturedCasesSection />
        <TestimonialCarouselSection />
        <GoogleReviewsSection />
        <VoiceSearchFAQSection />
        <PopularTagsSection />
        <section className="py-12 bg-gradient-to-br from-teal-50 to-emerald-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10 md:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">搬家知識與技巧</h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4">專業<strong>搬家公司</strong>分享實用的<strong>搬家技巧</strong>與注意事項，讓您的<strong>新北搬家</strong>、<strong>台北搬家</strong>更順利</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-8">
              <Link 
                to="/knowledge/article/7"
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all transform hover:-translate-y-2 overflow-hidden cursor-pointer group"
              >
                <div className="h-40 sm:h-48 overflow-hidden">
                  <OptimizedImage 
                    src="https://public.readdy.ai/ai/img_res/b048d8fad1d7fce27026a91c95b618c6.jpg"
                    alt="鋼琴搬運專業指南"
                    className="w-full h-full"
                  />
                </div>
                <div className="p-4 sm:p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="bg-teal-100 text-teal-600 px-3 py-1 rounded-full text-xs font-semibold">鋼琴搬運</span>
                    <span className="text-gray-500 text-xs sm:text-sm">7分鐘閱讀</span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">鋼琴搬運專業指南</h3>
                  <p className="text-gray-600 text-xs sm:text-sm mb-4">專業<strong>鋼琴搬運</strong>技巧，保護您的珍貴樂器安全搬遷</p>
                  <span className="text-teal-600 font-semibold text-xs sm:text-sm">
                    閱讀更多 <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </Link>

              <Link 
                to="/knowledge/article/8"
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all transform hover:-translate-y-2 overflow-hidden cursor-pointer group"
              >
                <div className="h-40 sm:h-48 overflow-hidden">
                  <OptimizedImage 
                    src="https://readdy.ai/api/search-image?query=Long%20distance%20moving%20truck%20on%20highway%20with%20cityscape%20in%20background%2C%20professional%20logistics%20and%20transportation%20concept%2C%20clean%20modern%20style%20with%20blue%20sky&width=600&height=400&seq=move008&orientation=landscape"
                    alt="跨縣市搬家注意事項"
                    className="w-full h-full"
                  />
                </div>
                <div className="p-4 sm:p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="bg-emerald-100 text-emerald-600 px-3 py-1 rounded-full text-xs font-semibold">長途搬家</span>
                    <span className="text-gray-500 text-xs sm:text-sm">8分鐘閱讀</span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">跨縣市搬家完全攻略</h3>
                  <p className="text-gray-600 text-xs sm:text-sm mb-4"><strong>長途搬家</strong>規劃與注意事項，讓您的<strong>跨縣市搬家</strong>順利無憂</p>
                  <span className="text-teal-600 font-semibold text-xs sm:text-sm">
                    閱讀更多 <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </Link>

              <Link 
                to="/knowledge"
                className="bg-gradient-to-br from-teal-600 to-emerald-600 rounded-xl shadow-md hover:shadow-xl transition-all transform hover:-translate-y-2 overflow-hidden cursor-pointer group p-6 sm:p-8 flex flex-col justify-center items-center text-center"
              >
                <div className="bg-white/20 w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-book-open-line text-white text-2xl sm:text-3xl"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-3">更多搬家知識</h3>
                <p className="text-white/90 text-sm sm:text-base mb-4">探索更多專業<strong className="text-white">搬家技巧</strong>與實用建議</p>
                <span className="text-white font-semibold bg-white/20 px-4 sm:px-6 py-2 rounded-full hover:bg-white/30 transition-colors text-sm sm:text-base">
                  查看全部文章 <i className="ri-arrow-right-line"></i>
                </span>
              </Link>
            </div>

            {/* 🎯 新增：常見問題快速解答區塊 */}
            <QuickFAQSection />

            {/* 🎯 新增：AI 口語化搜尋優化區塊 */}
            <AISearchOptimizedSection />
          </div>
        </section>

        {/* Quick Links Section - 📍 優化內部連結 */}
        <section className="py-12 md:py-20 bg-white rounded-2xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              <Link 
                to="/about"
                className="bg-gradient-to-br from-teal-500 to-emerald-500 text-white px-6 py-4 rounded-xl font-semibold hover:bg-teal-600 hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-information-line mr-2"></i>
                了解更多
              </Link>
              <Link 
                to="/pricing"
                className="bg-gradient-to-br from-emerald-500 to-teal-500 text-white px-6 py-4 rounded-xl font-semibold hover:bg-emerald-600 hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-price-tag-3-line mr-2"></i>
                查看價格
              </Link>
              <Link 
                to="/contact"
                className="bg-gradient-to-br from-teal-500 to-emerald-500 text-white px-6 py-4 rounded-xl font-semibold hover:bg-teal-600 hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-contacts-line mr-2"></i>
                聯絡我們
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section - 📍 地區關鍵字優化 */}
        <section className="py-12 md:py-20 bg-gradient-to-br from-teal-600 to-emerald-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 md:mb-6">準備開始您的<strong>新北搬家</strong>計畫？</h2>
            <p className="text-base sm:text-lg md:text-xl text-teal-100 mb-6 md:mb-10 px-4">
              立即聯絡我們，獲取免費專業評估與<strong>搬家報價</strong>
            </p>
            
            {/* 🎯 優化：LINE 按鈕更突出 */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8">
              <a 
                href="https://line.me/ti/p/@285yavdh"
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-green-600 text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-green-700 transition-all cursor-pointer whitespace-nowrap shadow-2xl hover:shadow-green-500/50 hover:scale-110 flex items-center justify-center gap-3 border-4 border-white/20"
              >
                <img 
                  src="https://static.readdy.ai/image/9ef47c451bf9aafd7a63eea67af7d95d/c33a3c3f2fa31df7dfad05bfabc09369.webp" 
                  alt="LINE" 
                  className="w-6 h-6 md:w-7 md:h-7"
                  loading="lazy"
                  decoding="async"
                />
                <span>加入官方 LINE 諮詢</span>
                <i className="ri-arrow-right-line text-2xl"></i>
              </a>
              <a 
                href="tel:0978900081"
                className="bg-white text-teal-600 px-6 py-4 rounded-full text-lg font-bold hover:bg-teal-50 transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                立即致電
              </a>
            </div>

            {/* 🎯 新增：LINE 優勢說明 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8">
              <h3 className="text-xl font-bold text-white mb-4">為什麼選擇 LINE 諮詢？</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
                <div className="flex items-start gap-3">
                  <div className="bg-green-500 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-white"></i>
                  </div>
                  <div>
                    <div className="text-white font-semibold">24小時快速回覆</div>
                    <div className="text-teal-100 text-sm">隨時隨地都能諮詢</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-green-500 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-image-line text-white"></i>
                  </div>
                  <div>
                    <div className="text-white font-semibold">傳送照片估價</div>
                    <div className="text-teal-100 text-sm">更精準的報價服務</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-green-500 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-line text-white"></i>
                  </div>
                  <div>
                    <div className="text-white font-semibold">即時預約時間</div>
                    <div className="text-teal-100 text-sm">快速確認搬家日期</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-green-500 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-question-answer-line text-white"></i>
                  </div>
                  <div>
                    <div className="text-white font-semibold">專業建議諮詢</div>
                    <div className="text-teal-100 text-sm">搬家疑問即時解答</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Media Links */}
            <div className="pt-6 md:pt-8 border-t border-white/20">
              <p className="text-white/80 mb-4 text-xs sm:text-sm">或透過社群媒體聯繫我們</p>
              <div className="flex gap-4 justify-center">
                <a 
                  href="https://www.facebook.com/profile.php?id=61584784501385&locale=zh_TW" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white/10 hover:bg-white/20 backdrop-blur-sm w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center transition-all transform hover:scale-110 cursor-pointer"
                >
                  <i className="ri-facebook-fill text-white text-xl md:text-2xl"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Service Areas Section - 📍 詳細地區資訊 - 恢復版本 390 */}
        <section className="py-12 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10 md:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
                <strong>搬家服務範圍</strong> - 新北台北桃園新竹
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4">
                主要服務<strong>新北市</strong>全區包含<strong>板橋區</strong>、<strong>新莊區</strong>、<strong>中和區</strong>、<strong>永和區</strong>、<strong>土城區</strong>、<strong>三重區</strong>、<strong>蘆洲區</strong>、<strong>泰山區</strong>、<strong>五股區</strong>、<strong>汐止區</strong>、<strong>新店區</strong>、<strong>淡水區</strong>、<strong>林口區</strong>等，以及<strong>台北市搬家</strong>、<strong>桃園搬家</strong>、<strong>新竹搬家</strong>
              </p>
            </div>

            {/* 🎯 新增：地區型長尾關鍵字網格 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {/* 新北市主要區域 */}
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-teal-600 w-12 h-12 rounded-full flex items-center justify-center">
                    <i className="ri-building-2-line text-white text-xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900"><strong>新北市搬家</strong></h3>
                </div>
                <div className="space-y-2 text-sm text-gray-700">
                  <Link to="/area/new-taipei" className="block hover:text-teal-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-teal-500"></i> <strong>板橋搬家</strong>｜板橋區搬家推薦
                  </Link>
                  <Link to="/area/new-taipei" className="block hover:text-teal-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-teal-500"></i> <strong>新莊搬家</strong>｜新莊區搬家公司
                  </Link>
                  <Link to="/area/new-taipei" className="block hover:text-teal-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-teal-500"></i> <strong>中和搬家</strong>｜中和區搬家費用
                  </Link>
                  <Link to="/area/new-taipei" className="block hover:text-teal-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-teal-500"></i> <strong>永和搬家</strong>｜永和區搬家價格
                  </Link>
                  <Link to="/area/new-taipei" className="block hover:text-teal-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-teal-500"></i> <strong>土城搬家</strong>｜土城區搬家服務
                  </Link>
                  <Link to="/area/new-taipei" className="block hover:text-teal-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-teal-500"></i> <strong>三重搬家</strong>｜三重區搬家推薦
                  </Link>
                </div>
              </div>

              {/* 台北市主要區域 */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-blue-600 w-12 h-12 rounded-full flex items-center justify-center">
                    <i className="ri-community-line text-white text-xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900"><strong>台北市搬家</strong></h3>
                </div>
                <div className="space-y-2 text-sm text-gray-700">
                  <Link to="/area/taipei" className="block hover:text-blue-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-blue-500"></i> <strong>大安區搬家</strong>｜大安搬家推薦
                  </Link>
                  <Link to="/area/taipei" className="block hover:text-blue-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-blue-500"></i> <strong>信義區搬家</strong>｜信義搬家公司
                  </Link>
                  <Link to="/area/taipei" className="block hover:text-blue-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-blue-500"></i> <strong>中山區搬家</strong>｜中山搬家費用
                  </Link>
                  <Link to="/area/taipei" className="block hover:text-blue-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-blue-500"></i> <strong>松山區搬家</strong>｜松山搬家價格
                  </Link>
                  <Link to="/area/taipei" className="block hover:text-blue-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-blue-500"></i> <strong>內湖區搬家</strong>｜內湖搬家服務
                  </Link>
                  <Link to="/area/taipei" className="block hover:text-blue-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-blue-500"></i> <strong>士林區搬家</strong>｜士林搬家推薦
                  </Link>
                </div>
              </div>

              {/* 桃園市主要區域 */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-purple-600 w-12 h-12 rounded-full flex items-center justify-center">
                    <i className="ri-map-pin-line text-white text-xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900"><strong>桃園市搬家</strong></h3>
                </div>
                <div className="space-y-2 text-sm text-gray-700">
                  <Link to="/area/taoyuan" className="block hover:text-purple-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-purple-500"></i> <strong>桃園區搬家</strong>｜桃園搬家推薦
                  </Link>
                  <Link to="/area/taoyuan" className="block hover:text-purple-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-purple-500"></i> <strong>中壢搬家</strong>｜中壢區搬家公司
                  </Link>
                  <Link to="/area/taoyuan" className="block hover:text-purple-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-purple-500"></i> <strong>平鎮搬家</strong>｜平鎮區搬家費用
                  </Link>
                  <Link to="/area/taoyuan" className="block hover:text-purple-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-purple-500"></i> <strong>八德搬家</strong>｜八德區搬家價格
                  </Link>
                  <Link to="/area/taoyuan" className="block hover:text-purple-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-purple-500"></i> <strong>蘆竹搬家</strong>｜蘆竹區搬家服務
                  </Link>
                  <Link to="/area/taoyuan" className="block hover:text-purple-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-purple-500"></i> <strong>龜山搬家</strong>｜龜山區搬家推薦
                  </Link>
                </div>
              </div>

              {/* 新竹市主要區域 */}
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-orange-600 w-12 h-12 rounded-full flex items-center justify-center">
                    <i className="ri-road-map-line text-white text-xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900"><strong>新竹市搬家</strong></h3>
                </div>
                <div className="space-y-2 text-sm text-gray-700">
                  <Link to="/area/hsinchu" className="block hover:text-orange-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-orange-500"></i> <strong>新竹東區搬家</strong>｜東區搬家推薦
                  </Link>
                  <Link to="/area/hsinchu" className="block hover:text-orange-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-orange-500"></i> <strong>新竹北區搬家</strong>｜北區搬家公司
                  </Link>
                  <Link to="/area/hsinchu" className="block hover:text-orange-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-orange-500"></i> <strong>竹北搬家</strong>｜竹北市搬家費用
                  </Link>
                  <Link to="/area/hsinchu" className="block hover:text-orange-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-orange-500"></i> <strong>新竹科學園區搬家</strong>
                  </Link>
                  <Link to="/area/hsinchu" className="block hover:text-orange-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-orange-500"></i> <strong>新竹工業區搬家</strong>
                  </Link>
                  <Link to="/area/hsinchu" className="block hover:text-orange-600 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-orange-500"></i> <strong>新竹辦公室搬遷</strong>
                  </Link>
                </div>
              </div>
            </div>

            {/* 🎯 新增：跨區搬家長尾關鍵字 */}
            <div className="bg-gradient-to-br from-gray-50 to-teal-50 rounded-2xl p-8 mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                <strong>跨區搬家服務</strong> - 熱門路線
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-all">
                  <i className="ri-arrow-left-right-line text-teal-600 mr-2"></i>
                  <strong>新北到台北搬家</strong>｜跨區搬家推薦
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-all">
                  <i className="ri-arrow-left-right-line text-teal-600 mr-2"></i>
                  <strong>台北到新北搬家</strong>｜雙北搬家服務
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-all">
                  <i className="ri-arrow-left-right-line text-teal-600 mr-2"></i>
                  <strong>新北到桃園搬家</strong>｜長途搬家費用
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-all">
                  <i className="ri-arrow-left-right-line text-teal-600 mr-2"></i>
                  <strong>台北到桃園搬家</strong>｜跨縣市搬家
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-all">
                  <i className="ri-arrow-left-right-line text-teal-600 mr-2"></i>
                  <strong>桃園到新竹搬家</strong>｜北部搬家服務
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-all">
                  <i className="ri-arrow-left-right-line text-teal-600 mr-2"></i>
                  <strong>新北到新竹搬家</strong>｜長途搬運推薦
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
              {/* 新北市主要區域 */}
              <div className="bg-gradient-to-br from-teal-50 to-emerald-100 p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-all">
                <div className="bg-teal-600 w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-building-2-line text-white text-xl md:text-2xl"></i>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4 text-center"><strong>新北市搬家</strong></h3>
                <p className="text-gray-600 text-center">
                  深耕<strong>新北市</strong>多年，熟悉各區巷弄、停車限制、電梯規範，提供最有效率的搬家路線規劃
                </p>
              </div>

              {/* 台北市 */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-100 p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-all">
                <div className="bg-emerald-600 w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-community-line text-white text-xl md:text-2xl"></i>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4 text-center"><strong>台北市搬家</strong></h3>
                <p className="text-gray-600 text-center">
                  深耕<strong>台北市</strong>多年，熟悉各區巷弄、停車限制、電梯規範，提供最有效率的搬家路線規劃
                </p>
              </div>

              {/* 桃園新竹 */}
              <div className="bg-gradient-to-br from-teal-50 to-emerald-100 p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-all">
                <div className="bg-teal-600 w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-map-pin-line text-white text-xl md:text-2xl"></i>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4 text-center"><strong>桃園新竹搬家</strong></h3>
                <p className="text-gray-600 text-center">
                  深耕<strong>桃園市</strong>、<strong>新竹市</strong>多年，熟悉各區巷弄、停車限制、電梯規範，提供最有效率的搬家路線規劃
                </p>
              </div>
            </div>

            {/* 🎯 新增：搬家類型長尾關鍵字 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                <strong>專業搬家服務類型</strong>
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Link to="/services" className="text-center p-4 bg-teal-50 rounded-lg hover:bg-teal-100 transition-all cursor-pointer">
                  <i className="ri-home-4-line text-3xl text-teal-600 mb-2"></i>
                  <div className="font-semibold text-gray-900"><strong>家庭搬家</strong></div>
                  <div className="text-xs text-gray-600 mt-1">住家搬遷服務</div>
                </Link>
                <Link to="/services" className="text-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-all cursor-pointer">
                  <i className="ri-building-line text-3xl text-blue-600 mb-2"></i>
                  <div className="font-semibold text-gray-900"><strong>辦公室搬遷</strong></div>
                  <div className="text-xs text-gray-600 mt-1">公司行號搬家</div>
                </Link>
                <Link to="/services" className="text-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-all cursor-pointer">
                  <i className="ri-store-line text-3xl text-purple-600 mb-2"></i>
                  <div className="font-semibold text-gray-900"><strong>店面搬遷</strong></div>
                  <div className="text-xs text-gray-600 mt-1">商店搬家服務</div>
                </Link>
                <Link to="/services" className="text-center p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-all cursor-pointer">
                  <i className="ri-truck-line text-3xl text-orange-600 mb-2"></i>
                  <div className="font-semibold text-gray-900"><strong>長途搬家</strong></div>
                  <div className="text-xs text-gray-600 mt-1">跨縣市搬運</div>
                </Link>
                <Link to="/services" className="text-center p-4 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-all cursor-pointer">
                  <i className="ri-music-line text-3xl text-emerald-600 mb-2"></i>
                  <div className="font-semibold text-gray-900"><strong>鋼琴搬運</strong></div>
                  <div className="text-xs text-gray-600 mt-1">精密樂器搬運</div>
                </Link>
                <Link to="/services" className="text-center p-4 bg-pink-50 rounded-lg hover:bg-pink-100 transition-all cursor-pointer">
                  <i className="ri-delete-bin-line text-3xl text-pink-600 mb-2"></i>
                  <div className="font-semibold text-gray-900"><strong>廢棄物處理</strong></div>
                  <div className="text-xs text-gray-600 mt-1">垃圾清運服務</div>
                </Link>
                <Link to="/services" className="text-center p-4 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-all cursor-pointer">
                  <i className="ri-box-3-line text-3xl text-indigo-600 mb-2"></i>
                  <div className="font-semibold text-gray-900"><strong>精緻包裝</strong></div>
                  <div className="text-xs text-gray-600 mt-1">專業打包服務</div>
                </Link>
                <Link to="/services" className="text-center p-4 bg-amber-50 rounded-lg hover:bg-amber-100 transition-all cursor-pointer">
                  <i className="ri-tools-line text-3xl text-amber-600 mb-2"></i>
                  <div className="font-semibold text-gray-900"><strong>家具拆裝</strong></div>
                  <div className="text-xs text-gray-600 mt-1">家具組裝服務</div>
                </Link>
              </div>
            </div>

            <div className="text-center">
              <Link 
                to="/service-area"
                className="inline-block bg-teal-600 text-white px-6 md:px-8 py-3 rounded-full text-base md:text-lg font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看詳細搬家服務範圍
              </Link>
            </div>
          </div>
        </section>

        {/* 📍 在地服務特色區塊 */}
        <section className="py-12 md:py-20 bg-gradient-to-br from-gray-50 to-teal-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10 md:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
                <strong>在地服務優勢</strong>
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4">
                深耕<strong>新北市</strong>、<strong>台北市</strong>在地搬家服務，了解當地特性
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* 在地優勢1 */}
              <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-all">
                <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-map-2-line text-teal-600 text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">熟悉在地環境</h3>
                <p className="text-gray-600 text-center">
                  深耕<strong>新北市</strong>、<strong>台北市</strong>多年，熟悉各區巷弄、停車限制、電梯規範，提供最有效率的搬家路線規劃
                </p>
              </div>

              {/* 在地優勢2 */}
              <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-all">
                <div className="bg-emerald-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-time-line text-emerald-600 text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">快速到達服務</h3>
                <p className="text-gray-600 text-center">
                  位於<strong>新北市泰山區</strong>，可快速到達<strong>新北各區</strong>、<strong>台北市</strong>，緊急搬家需求也能及時響應
                </p>
              </div>

              {/* 在地優勢3 */}
              <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-all">
                <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-customer-service-2-line text-teal-600 text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">在地口碑推薦</h3>
                <p className="text-gray-600 text-center">
                  在<strong>新北</strong>、<strong>台北</strong>地區累積超過<strong>5000位客戶</strong>信賴，在地口碑推薦，鄰里間的好評見證
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default HomePage;